import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { DownloadCloud02Icon, RefreshCcw01Icon } from '@/components/ui/icons'
import { Tooltip } from '@/components/ui/tooltip'
import { ResolutionWorklistDialog } from '@/features/marquee/components/resolution-worklist-dialog'
import { ResolveEventDialog } from '@/features/marquee/components/resolve-event-dialog'
import {
	attendeesQueryOptions,
	exportPreviewQueryOptions,
	operatorQueryOptions
} from '@/features/marquee/query-options'
import {
	isMarketManagerAllowed,
	resolutionWorklistRows
} from '@/features/marquee/utils'
import type { MarqueeAttendee, MarqueeEventDetail } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/**
 * The worklist half, in four states: withheld with no explanation while the
 * roster has not landed (only the export tab and the attendee detail reach
 * this without it already cached, and "nothing to fix" would be a false
 * reason), a retry when the roster failed to load (the export tab has no
 * other fetch of it, so nothing else would bring it back), the download
 * behind its confirmation once there is something to download, and withheld
 * with the reason once there is not.
 */
function WorklistButton({
	event,
	rows,
	onRetry
}: {
	event: MarqueeEventDetail
	/** `undefined` while the roster is loading or failed. */
	rows: MarqueeAttendee[] | undefined
	/** Set once the roster failed: the press refetches it. */
	onRetry?: () => void
}) {
	const { t } = useTranslation()
	const label = (
		<>
			<DownloadCloud02Icon />
			{t('marquee:resolution_worklist_button')}
		</>
	)

	if (rows === undefined) {
		return onRetry ? (
			<Tooltip
				content={t('marquee:resolution_worklist_error_tooltip')}
				maxWidth={280}
				triggerProps={{ asChild: true }}
			>
				<Button
					className='flex-1 l:flex-none'
					onClick={onRetry}
					size='sm'
					type='button'
					variant='secondaryOutline'
				>
					{label}
				</Button>
			</Tooltip>
		) : (
			<Button
				className='flex-1 l:flex-none'
				disabled
				size='sm'
				type='button'
				variant='secondaryOutline'
			>
				{label}
			</Button>
		)
	}

	if (rows.length > 0) {
		return (
			<ResolutionWorklistDialog
				event={event}
				rows={rows}
				trigger={
					<Button
						className='flex-1 l:flex-none'
						size='sm'
						type='button'
						variant='secondaryOutline'
					>
						{label}
					</Button>
				}
			/>
		)
	}

	return (
		<Tooltip
			content={t('marquee:resolution_worklist_empty_tooltip')}
			maxWidth={280}
			triggerProps={{ asChild: true }}
		>
			{/* aria-disabled rather than disabled: a disabled button gets no
			    pointer events, so the tooltip that explains it would never
			    open. */}
			<Button
				aria-disabled
				className='flex-1 l:flex-none'
				size='sm'
				type='button'
				variant='secondaryOutline'
			>
				{label}
			</Button>
		</Tooltip>
	)
}

/**
 * The bulk resolution loop's two controls, on the event header so they reach
 * every tab: **Download account worklist** (the host IDs still waiting on an account,
 * in the lookup table's columns) and **Refresh accounts** (re-run the resolver
 * against the corrected table). Fixing one row already works from the roster's
 * menu; this is the path for the two hundred rows of a market that imported
 * before its accounts were loaded, while the event is under way.
 *
 * Creator-only, hidden rather than disabled for a facilitator: both halves of
 * the loop (this re-run and the lookup table upload it depends on) are creator
 * operations, so a worklist alone would be work they cannot finish.
 *
 * State: the re-run works in `pre_work`, `live` and `closed`, and `locked`
 * refuses it. The backend's 409 does not say why, so the reason is derived here
 * from `event.state` and shown on a withheld button instead of after a press.
 *
 * The roster is read from the shared cache (the attendees and check-in tabs
 * already subscribe to it) and is what the re-run's response overwrites, so
 * the worklist count moves the moment a refresh lands.
 */
function ResolutionActions({ event }: { event: MarqueeEventDetail }) {
	const { t } = useTranslation()

	const { data: operator } = useQuery(operatorQueryOptions())
	const isAllowed = isMarketManagerAllowed(operator, 'resolveEvent')

	const attendeesOptions = attendeesQueryOptions(event.id)
	const {
		data: attendees,
		isError: isRosterError,
		refetch: refetchRoster
	} = useQuery({
		...attendeesOptions,
		enabled: attendeesOptions.enabled && isAllowed
	})
	const worklist = useMemo(
		() => resolutionWorklistRows(attendees?.rows ?? []),
		[attendees?.rows]
	)

	// Only a closed event has a preview, and only the preview knows whether a
	// CSV has gone out: the confirmation warns about Salesforce then.
	const isClosed = event.state === 'closed'
	const previewOptions = exportPreviewQueryOptions(event.id)
	const { data: preview } = useQuery({
		...previewOptions,
		enabled: previewOptions.enabled && isAllowed && isClosed
	})
	const hasExported = Boolean(preview?.last_export)

	if (!isAllowed) {
		return null
	}

	const isLocked = event.state === 'locked'

	return (
		<div className='flex l:w-auto w-full items-center gap-2'>
			<WorklistButton
				event={event}
				onRetry={isRosterError ? () => refetchRoster() : undefined}
				rows={attendees && worklist}
			/>

			{isLocked ? (
				<Tooltip
					content={t('marquee:resolve_event_locked_tooltip')}
					maxWidth={280}
					triggerProps={{ asChild: true }}
				>
					<Button
						aria-disabled
						className='flex-1 l:flex-none'
						size='sm'
						type='button'
						variant='secondary'
					>
						<RefreshCcw01Icon />
						{t('marquee:resolve_event_button')}
					</Button>
				</Tooltip>
			) : (
				<ResolveEventDialog
					event={event}
					hasExported={hasExported}
					trigger={
						<Button
							className='flex-1 l:flex-none'
							size='sm'
							type='button'
							variant='secondary'
						>
							<RefreshCcw01Icon />
							{t('marquee:resolve_event_button')}
						</Button>
					}
				/>
			)}
		</div>
	)
}

export { ResolutionActions }
